import {useCallback, useRef, useState, type ChangeEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {DEFAULT_QR_LOGO_URL, type QrLogoState} from '../../../common/utils/qrRenderer'
import {QR_LOGO_MARGIN_RANGE, QR_LOGO_SIZE_RANGE} from '../../../common/constants/qrStyleOptions'
import {loadQrLogoFile, QrLogoLoadError} from '../../../common/utils/loadQrLogoFile'
import {trackEvent} from '../../../common/analytics/trackEvent'

interface QrLogoPickerProps {
  logo: QrLogoState
  onChange: (patch: Partial<QrLogoState>) => void
}

const QrLogoPicker = ({logo, onChange}: QrLogoPickerProps) => {
  const {t} = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const handleToggle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const enabled = e.target.checked
      onChange({enabled})
      trackEvent('qr_logo_toggled', {enabled})
    },
    [onChange],
  )

  const handlePick = useCallback(() => fileInputRef.current?.click(), [])

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return
      setError(null)
      setBusy(true)
      try {
        const dataUrl = await loadQrLogoFile(file)
        onChange({imageUrl: dataUrl})
        trackEvent('qr_logo_uploaded', {size_bytes: file.size, format: file.type})
      } catch (err) {
        setError(err instanceof QrLogoLoadError ? t(`qrGenerator.logo.${err.key}`) : t('qrGenerator.logo.failed'))
      } finally {
        setBusy(false)
      }
    },
    [onChange, t],
  )

  const handleUseDefault = useCallback(() => onChange({imageUrl: null}), [onChange])

  const previewSrc = logo.imageUrl ?? DEFAULT_QR_LOGO_URL
  const isCustom = logo.imageUrl !== null

  return (
    <div className="qrg-logo">
      <label className="qrg-toggle">
        <input type="checkbox" checked={logo.enabled} onChange={handleToggle} />
        {t('qrGenerator.logo.enable')}
      </label>

      {logo.enabled && (
        <>
          <div className="qrg-logo-row">
            <div className="qrg-logo-thumb">
              <img src={previewSrc} alt={t('qrGenerator.logo.previewAlt')} />
            </div>
            <div className="qrg-logo-actions">
              <button type="button" className="qrg-btn qrg-btn-secondary" onClick={handlePick} disabled={busy}>
                {busy ? t('qrGenerator.logo.busy') : t('qrGenerator.logo.upload')}
              </button>
              {isCustom && (
                <button type="button" className="qrg-btn qrg-btn-ghost" onClick={handleUseDefault}>
                  {t('qrGenerator.logo.useDefault')}
                </button>
              )}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={handleFileChange}
            style={{display: 'none'}}
          />
          {error && <p className="qrg-error">{error}</p>}

          <label className="qrg-slider">
            <span className="qrg-slider-label">{t('qrGenerator.logo.size')}</span>
            <input
              type="range"
              min={QR_LOGO_SIZE_RANGE.min}
              max={QR_LOGO_SIZE_RANGE.max}
              step={QR_LOGO_SIZE_RANGE.step}
              value={logo.size}
              onChange={(e) => onChange({size: parseFloat(e.target.value)})}
            />
            <span className="qrg-slider-value">{Math.round(logo.size * 100)}%</span>
          </label>
          <label className="qrg-slider">
            <span className="qrg-slider-label">{t('qrGenerator.logo.margin')}</span>
            <input
              type="range"
              min={QR_LOGO_MARGIN_RANGE.min}
              max={QR_LOGO_MARGIN_RANGE.max}
              step={QR_LOGO_MARGIN_RANGE.step}
              value={logo.margin}
              onChange={(e) => onChange({margin: parseInt(e.target.value, 10)})}
            />
            <span className="qrg-slider-value">{logo.margin}px</span>
          </label>
          <p className="qrg-hint">{t('qrGenerator.logo.tip')}</p>
        </>
      )}
    </div>
  )
}

export default QrLogoPicker
